import { Menu, Transition } from '@headlessui/react';
import { NodeModel, useDragOver } from '@minoru/react-dnd-treeview';
import { FocusEvent, Fragment, KeyboardEvent, useCallback, useRef, useState } from 'react';

import {
  CaretDownIcon,
  DeleteIcon,
  LinkIcon,
  MoreIcon,
  NodeLinkAddIcon,
  NodePageAddIcon,
  RenameIcon,
  UnlockIcon,
} from '~components/icons';
import { useLabel } from '~hooks/useLabels';
import { StatusChip } from './StatusChip';
import { TypeIcon } from './TypeIcon';
import { CustomData } from './types';
import { calcIndent } from './utils';

type Props = {
  node: NodeModel<CustomData>;
  depth: number;
  isOpen: boolean;
  hasChild: boolean;
  onToggle: (id: NodeModel['id']) => void;
  onTextChange: (id: NodeModel['id'], value: string) => void;
  onLink: (id: NodeModel['id']) => void;
  onRemove: (id: NodeModel['id']) => void;
  onAddPage: (id: NodeModel['id']) => void;
  onAddGroup: (id: NodeModel['id']) => void;
  onManagePermissions: (id: NodeModel['id']) => void;
};

export const CustomNode = ({
  node,
  depth,
  isOpen,
  onToggle,
  hasChild,
  onTextChange,
  onLink,
  onRemove,
  onAddPage,
  onAddGroup,
  onManagePermissions,
}: Props) => {
  const { id, text: title, data } = node;
  const indent = calcIndent(depth);

  function handleRemove() {
    onRemove?.(id);
  }

  function handleAddPage() {
    onAddPage?.(id);
  }

  function handleAddGroup() {
    onAddGroup?.(id);
  }

  function handleManagePermissions() {
    onManagePermissions?.(id);
  }

  const handleLinkClick = () => {
    onLink?.(id);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.(id);
  };

  const handleRename = useCallback(
    (value: string) => {
      onTextChange?.(id, value);
    },
    [id, onTextChange],
  );

  const dragOverProps = useDragOver(id, isOpen, onToggle);

  // Correct permissions with regad to hasChild - Only allow delete if node has no children
  const permissions = { ...data.permissions, canDelete: data.permissions.canDelete && !hasChild };

  return (
    <div
      className="leaf--root flex items-center min-h-8 max-h-8"
      style={{ paddingInlineStart: indent }}
      {...dragOverProps}
    >
      <div className="w-8 h-8 flex items-center justify-center">
        {hasChild && (
          <div className="w-4 h-4 cursor-pointer" onClick={handleToggle}>
            <div className={`w-full h-full transform ${!isOpen && '-rotate-90'}`}>
              <CaretDownIcon />
            </div>
          </div>
        )}
      </div>
      <NodeItem
        title={title}
        changed={data.changed}
        type={data.type}
        isUnpublished={data.isUnpublished}
        hasDraft={data.hasDraft}
        permissions={permissions}
        onRename={handleRename}
        onLink={handleLinkClick}
        onRemove={handleRemove}
        onAddPage={handleAddPage}
        onAddGroup={handleAddGroup}
        onManagePermissions={handleManagePermissions}
      />
    </div>
  );
};

type NodeItemProps = {
  title: string;
  changed?: boolean;
  type: string;
  isUnpublished: boolean;
  hasDraft: boolean;
  permissions?: CustomData['permissions'];
  onRename: (value: string) => void;
  onLink: () => void;
  onRemove: () => void;
  onAddPage: () => void;
  onAddGroup: () => void;
  onManagePermissions: () => void;
};

function NodeItem({
  title,
  changed,
  type,
  isUnpublished,
  hasDraft,
  permissions,
  onRename,
  onLink,
  onRemove,
  onAddPage,
  onAddGroup,
  onManagePermissions,
}: NodeItemProps) {
  const [editTitle, setEditTitle] = useState(false);

  const handleRename = useCallback(
    (value: string) => {
      setEditTitle(false);
      if (value === '' || value === title) {
        return;
      }
      onRename(value);
    },
    [onRename, title],
  );

  return (
    <div className="flex items-center h-8 bg-white border border-solid border-gray shadow-md w-full transition flex-grow justify-between relative">
      {changed && (
        <div className="h-2 w-2 bg-edit rounded-full absolute left-0 top-0 transform -translate-x-1 -translate-y-1" />
      )}
      <div className="text-xs w-4 h-4 text-darkgray mx-2">
        <TypeIcon type={type} />
      </div>
      <div className="h-8 items-center flex flex-grow">
        <NodeTitle title={title} onChange={handleRename} edit={editTitle} />
      </div>
      <div className="flex items-center justify-center">
        {changed && <StatusChip label="labelMenuElementIsChanged" highlight />}
        {isUnpublished && <StatusChip label="labelMenuElementIsUnpublished" />}
        {hasDraft && <StatusChip label="labelMenuElementHasDraft" />}
        <ContextMenu
          {...permissions}
          onRenameClick={() => setEditTitle(true)}
          onLinkClick={onLink}
          onDeleteClick={onRemove}
          onAddPageClick={onAddPage}
          onAddGroupClick={onAddGroup}
          onManagePermissionsClick={onManagePermissions}
        />
      </div>
    </div>
  );
}

type NodeTitleProps = {
  title: string;
  edit: boolean;
  onChange: (value: string) => void;
};
function NodeTitle({ title, edit, onChange }: NodeTitleProps) {
  if (edit) {
    return <NodeTitleInput value={title} onChange={onChange} />;
  }
  return <div className="text-sm text-black">{title}</div>;
}

type NodeTitleInputProps = {
  value: string;
  onChange: (value: string) => void;
};

function NodeTitleInput({ value, onChange }: NodeTitleInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    const value = (event.target.value || '').trim();
    onChange(value);
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    // auto select all text if unchanged
    if (event.target.value === value) {
      event.target.setSelectionRange(0, event.target.value.length);
    }
  }

  function handleKeyPress(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === 'Escape') {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }

  return (
    <input
      ref={ref}
      className="appearance-none rounded font-sans text-sm text-black p-1 m-0 outline-none border-solid border-2 border-edit"
      defaultValue={value}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyPress={handleKeyPress}
    />
  );
}

type ContextMenuProps = {
  canView?: boolean;
  canRename?: boolean;
  canCreate?: boolean;
  canManagePermissions?: boolean;
  canDelete?: boolean;
  onLinkClick: () => void;
  onRenameClick: () => void;
  onDeleteClick: () => void;
  onAddPageClick: () => void;
  onAddGroupClick: () => void;
  onManagePermissionsClick: () => void;
};

function ContextMenu({
  canView = false,
  canRename = false,
  canCreate = false,
  canManagePermissions = false,
  canDelete = false,
  onLinkClick,
  onRenameClick,
  onDeleteClick,
  onAddPageClick,
  onAddGroupClick,
  onManagePermissionsClick,
}: ContextMenuProps) {
  return (
    <Menu as="div" className="relative inline-block text-left mr-2">
      <div>
        <Menu.Button className="appearance-none cursor-pointer inline-flex items-center justify-center rounded bg-transparent text-sm border-none outline-none p-1 m-0 hover:bg-gray">
          <MoreIcon className="w-4 h-4 text-darkgray" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute z-20 right-0 origin-top-right rounded bg-white shadow-lg">
          <div className="p-1">
            <MenuItem onClick={onLinkClick} Icon={LinkIcon} label="btnEditMenuElementOpen" disabled={!canView} />
            <MenuItem
              onClick={onRenameClick}
              Icon={RenameIcon}
              label="btnEditMenuElementRename"
              disabled={!canRename}
            />
            <MenuItem
              onClick={onAddPageClick}
              Icon={NodePageAddIcon}
              label="btnEditMenuElementAddPage"
              disabled={!canCreate}
            />
            <MenuItem
              onClick={onAddGroupClick}
              Icon={NodeLinkAddIcon}
              label="btnEditMenuElementAddGroup"
              disabled={!canCreate}
            />
            <MenuItem
              onClick={onManagePermissionsClick}
              Icon={UnlockIcon}
              label="btnEditMenuElementManagePermissions"
              disabled={!canManagePermissions}
            />
            <MenuItem
              onClick={onDeleteClick}
              Icon={DeleteIcon}
              label="btnEditMenuElementDelete"
              disabled={!canDelete}
            />
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

type MenuItemProps = {
  Icon: any;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
};

function MenuItem({ Icon, label: labelId, disabled, onClick }: MenuItemProps) {
  const label = useLabel(labelId);
  function handleClick() {
    if (!disabled) {
      onClick?.();
    }
  }

  return (
    <Menu.Item disabled={disabled}>
      <button
        disabled={disabled}
        onClick={handleClick}
        className="appearance-none flex w-full items-center whitespace-nowrap text-sm border-none outline-none bg-transparent transition cursor-pointer py-1 hover:bg-gray"
      >
        <Icon className="w-4 h-4 mr-2" /> {label}
      </button>
    </Menu.Item>
  );
}
